// AssignLeadsModal.tsx — assign the selected leads to a staff member (admins).
// POST /v1/leads/assign { lead_ids, staff_id }. Same rule as the app:
// converted leads (and old "closed" ones) are left out.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Check, Loader2, Search, UserPlus, X } from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';

export interface AssignableLead {
  id: string;
  name: string | null;
  status: string | null;
  assigned_to: { id: string | null; email: string | null } | null;
}

interface StaffMember {
  id: string;
  email: string;
  name?: string | null;
  role: string;
  status?: number; // 1 active, 0 inactive
}

const EXCLUDED_STATUSES = ['converted', 'closed'];
const SEARCH_DELAY_MS = 300;

interface AssignLeadsModalProps {
  visible: boolean;
  leads: AssignableLead[];
  onHide: () => void;
  onAssigned: (staffEmail: string, count: number) => void;
}

const AssignLeadsModal: React.FC<AssignLeadsModalProps> = ({ visible, leads, onHide, onAssigned }) => {
  const [query, setQuery] = useState('');
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<StaffMember | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);

  const assignable = useMemo(() => leads.filter((l) => !EXCLUDED_STATUSES.includes((l.status ?? '').toLowerCase())), [leads]);
  const skipped = leads.length - assignable.length;
  const reassigning = assignable.filter((l) => l.assigned_to?.id && l.assigned_to.id !== picked?.id).length;

  useEffect(() => {
    if (visible) {
      setQuery('');
      setPicked(null);
      setError(null);
    }
  }, [visible]);

  // Staff list (server-side search)
  useEffect(() => {
    if (!visible) return;
    const id = ++reqId.current;
    const run = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await apiClient.get('/v1/staff', { params: { page: 1, limit: 50, ...(query.trim() ? { search: query.trim() } : {}) } });
        if (id !== reqId.current) return;
        setStaff((res.data?.data ?? []).filter((s: StaffMember) => s.role === 'staff'));
      } catch (e: any) {
        if (id === reqId.current) setLoadError(e?.response?.data?.error || 'Could not load staff.');
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    };
    const t = setTimeout(run, query.trim() ? SEARCH_DELAY_MS : 0);
    return () => clearTimeout(t);
  }, [visible, query]);

  const handleHide = () => {
    if (!saving) onHide();
  };

  const assign = async () => {
    if (!picked || assignable.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.post('/v1/leads/assign', { lead_ids: assignable.map((l) => l.id), staff_id: picked.id });
      onAssigned(picked.email, res.data?.matched_count ?? assignable.length);
      onHide();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not assign the leads.');
    } finally {
      setSaving(false);
    }
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
          color: C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <UserPlus size={19} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>Assign leads</div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          {assignable.length} lead{assignable.length === 1 ? '' : 's'} to a staff member
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="alm-dialog"
      maskClassName="alm-mask"
      style={{ width: '92vw', maxWidth: '480px' }}
      modal
      closable={!saving}
      dismissableMask={!saving}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .alm-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; }
        .alm-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; }
        .alm-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .alm-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
        .alm-row:hover { background: ${G.shellBg} !important; }
        .alm-spin { animation: almSpin 0.9s linear infinite; }
        @keyframes almSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {/* search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', height: '38px', padding: '0 11px', border: `1px solid ${G.line}`, borderRadius: '8.3px' }}>
          <Search size={13} color={C.muted} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search staff by name or email"
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.76rem', color: '#111827' }}
          />
          {loading && <Loader2 size={13} color={C.muted} className="alm-spin" />}
        </div>

        {/* staff list */}
        <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${G.line}`, borderRadius: '9.2px' }}>
          {loadError ? (
            <div style={{ padding: '14px', fontSize: '0.72rem', color: '#c62828' }}>{loadError}</div>
          ) : !loading && staff.length === 0 ? (
            <div style={{ padding: '14px', fontSize: '0.72rem', color: C.muted }}>No staff found.</div>
          ) : (
            staff.map((s, i) => {
              const inactive = s.status === 0;
              const on = picked?.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className="alm-row"
                  disabled={inactive || saving}
                  onClick={() => setPicked(s)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 12px',
                    border: 'none',
                    borderTop: i ? `1px solid ${G.softer}` : 'none',
                    background: on ? C.limeDim : 'white',
                    textAlign: 'left',
                    cursor: inactive ? 'not-allowed' : 'pointer',
                    opacity: inactive ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: C.teal,
                      color: 'white',
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {(s.name || s.email).slice(0, 2).toUpperCase()}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.name || s.email.split('@')[0]}
                    </span>
                    <span style={{ fontSize: '0.64rem', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.email}
                      {inactive ? ' · inactive' : ''}
                    </span>
                  </span>
                  {on && <Check size={15} color="#4a5e00" strokeWidth={3} />}
                </button>
              );
            })
          )}
        </div>

        {(skipped > 0 || reassigning > 0) && (
          <div style={{ fontSize: '0.66rem', color: '#92400e', background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.35)', borderRadius: '8px', padding: '8px 10px', lineHeight: 1.5 }}>
            {skipped > 0 && `${skipped} converted lead${skipped === 1 ? ' is' : 's are'} left out. `}
            {reassigning > 0 && `${reassigning} lead${reassigning === 1 ? ' is' : 's are'} already assigned to someone else and will move.`}
          </div>
        )}
        {error && <div style={{ fontSize: '0.7rem', color: '#c62828', fontWeight: 600 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px' }}>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={handleHide} disabled={saving} />
          <button
            type="button"
            onClick={assign}
            disabled={!picked || saving || assignable.length === 0}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '0 18px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 800,
              cursor: !picked || saving ? 'not-allowed' : 'pointer',
              opacity: !picked || assignable.length === 0 ? 0.5 : 1,
            }}
          >
            {saving ? <Loader2 size={14} className="alm-spin" /> : <UserPlus size={14} />}
            {saving ? 'ASSIGNING…' : `ASSIGN ${assignable.length}`}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AssignLeadsModal;
